/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';

// Mantine
import {
	Alert,
	Avatar,
	Box,
	Divider,
	Flex,
	Group,
	Image,
	NumberFormatter,
	Paper,
	Skeleton,
	Spoiler,
	Stack,
	Tabs,
	Tooltip,
} from '@mantine/core';
import {
	useElementSize,
	useMediaQuery,
} from '@mantine/hooks';

// Styles
import classes from './measurability-project-details.module.css';
import classesTabs from './tabs.module.css';
// Router
import { useParams } from 'react-router';

// I18n
import { useTranslation } from 'react-i18next';

// Api
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/details/api/get-measurability-project-by-id';

// Components

// Icons
import {
	IconInfoCircle,
	IconQuestionMark,
} from '@tabler/icons-react';

// local components
import { SitesByProject } from '@features/catalogs/meausrability-projects/details/components/SitesByProject';
import './tabs.css';

// constants
import { DescriptionRenderer } from '@components/atoms/DescriptionRenderer';
import { Button, Title } from '@components/v2/ui';
import { DownloadTag } from '@components/v2/ui/DownloadTag/DownloadTag';
import { IconProjectLocation } from '@components/v2/ui/IconProjectLocation/IconProjectLocation';
import { StatusSwitch } from '@components/v2/ui/StatusSwitch';
import { ACLS } from '@config/constants/acls';
import { NOT_APPLICABLE } from '@config/constants/common';
import { ROLES } from '@config/constants/roles';
import { MintHistoryList } from '@features/catalogs/meausrability-projects/details/components/MintHistoryList';
import { TSiteCustom } from '@features/catalogs/meausrability-projects/details/components/Sites/Sites';
import { useActivateMeasurabilityProject } from '@features/resources/measurabilities/api/activate-measurability-project';
import {
	TFileMetadata,
	TSite,
} from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import useStore from '@store/index';
import { checkAclFront } from '@utils/acl-front';
import { getCountryName } from '@utils/countries';
import { getUnitLabel } from '@utils/units';
import { useSites } from 'shared/api/get-sites';
import { Text } from '../../../../../../components/v2/ui/Text';

const DEFAULT_COVER =
	'https://static.vecteezy.com/system/resources/thumbnails/028/142/383/small_2x/a-stadium-filled-with-excited-fans-a-football-field-in-the-foreground-background-with-empty-space-for-text-photo.jpg';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MeasurabilityProjectDetails = () => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	const isSmall = useMediaQuery('(max-width: 768px)');

	const { ref } = useElementSize();

	const { projectId } = useParams();

	const { checkRole, checkAcls, role } =
		useAuthorization();

	const [siteQuery, setSiteQuery] = useState('');

	const icon = <IconInfoCircle />;

	const [isExpanded, setIsExpanded] = useState(false);

	const { data: allSites } = useSites({}, siteQuery);

	const {
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams,
		setMeasurabilitiesSelectedRow,
	} = useStore();

	const { mutate: mutateActivateMeasurabilityProject } =
		useActivateMeasurabilityProject(
			measurabilityProjectsPage,
			measurabilityProjectsPageSize,
			measurabilityProjectsQueryParams,
			''
		);

	const {
		data,
		isFetching,
		isError,
		contactProducer,
		sites,
	} = useMeasurabilityProject({
		projectId: projectId ? projectId : '',
	});

	if (isError) {
		return (
			<Box className={classes.container} ref={ref}>
				<Paper className={classes.paperError}>
					<Alert
						variant="light"
						color="error"
						title={t('errors.invalidProjectIdTitle')}
						icon={icon}
					>
						{t('errors.invalidProjectId')}
					</Alert>
				</Paper>
			</Box>
		);
	}

	if (isFetching) {
		return (
			<Box className={classes.containerSkeletons}>
				<Stack gap={'25px'}>
					<Skeleton
						height={'300px'}
						width={'100%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'200px'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'100%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'50%'}
						radius="md"
					/>
				</Stack>
			</Box>
		);
	}

	const measurabilityProject = data;

	if (!measurabilityProject) return null;

	const {
		cover,
		name,
		technology,
		sellPrice_europertco2eq,
		description,
		entity,
		location,
		publicSpecs,
		supportedByName,
		supportedBy,
		remaining_aco2,
	} = measurabilityProject;

	function getSearchableSites(
		sites: TSiteCustom[],
		siteIds: string[]
	) {
		if (Array.isArray(sites) && Array.isArray(siteIds)) {
			const result = sites.filter((_site) =>
				siteIds.includes(_site.id)
			);
			return result;
		}
		return [];
	}

	return (
		<Box className={classes.container} ref={ref}>
			<Box
				style={{
					padding: '24px',
					display: 'flex',
					flex: '1',
					flexDirection: 'column',
					minHeight: '304px',
					maxHeight: 'fit-content',
					borderRadius: '32px',
					position: 'relative',
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
					backgroundImage: `
      linear-gradient(0deg, rgba(15, 23, 42, 0.00) 0%, #0F172A 104.61%),
      url(${cover ?? DEFAULT_COVER})
    `,
				}}
			>
				<Flex gap={'20px'}>
					{supportedBy && (
						<Flex>
							<Image
								src={supportedBy}
								w={'89px'}
								h={'89px'}
								radius={'xl'}
								style={{
									border: '4px solid #F0F3F8',
								}}
							/>
						</Flex>
					)}
					<Flex direction={'column'} flex={1} gap={'2px'}>
						<Flex wrap={'wrap'}>
							<Flex flex={1} wrap={'wrap'} gap={'20px'}>
								<Flex flex={1} direction={'column'}>
									{supportedByName &&
										supportedByName != NOT_APPLICABLE && (
											<Flex>
												<Text
													variant="body2"
													c="var(--mantine-color-igluTextButton-9)"
												>
													{t('global.projectSupportedBy')}{' '}
													<Text
														span
														variant="body2-medium"
														c="var(--mantine-color-igluSurface-9)"
													>
														{supportedByName}
													</Text>
												</Text>
											</Flex>
										)}
									<Flex>
										<Title
											order={4}
											c="var(--mantine-color-igluSurface-9)"
										>
											{name}
										</Title>
									</Flex>
									<Flex gap={'16px'}>
										<Group justify="center" gap={'5px'}>
											<Avatar
												radius={'xl'}
												src={`https://flagcdn.com/w40/${location?.countryCode?.toLowerCase()}.png`}
												size={18}
												style={{
													border: '1px solid #D7D6D6',
												}}
											>
												<IconQuestionMark size={18} />
											</Avatar>

											<Box>
												<Text
													variant={'caption'}
													c={
														'var(--mantine-color-igluSurface-9)'
													}
												>
													{getCountryName(
														location.countryCode
													)}
												</Text>
											</Box>
										</Group>
										<Group gap={'5px'}>
											<IconProjectLocation
												color={'#FDFDFD'}
											/>

											<Box>
												<Text
													variant={'caption'}
													c={
														'var(--mantine-color-igluSurface-9)'
													}
												>
													{location?.place ||
														NOT_APPLICABLE}
												</Text>
											</Box>
										</Group>

										{!checkRole([ROLES.BRAND_ADMIN]) && (
											<StatusSwitch
												inActiveLabel={t(
													'global.inactive'
												)}
												activeLabel={t('global.active')}
												active={
													measurabilityProject?.active
												}
												readonly={checkRole([
													ROLES.PRODUCER_ADMIN,
													ROLES.PRODUCER_STAFF,
													ROLES.ORGANIZATION_ADMIN,
												])}
												onChange={async () => {
													setMeasurabilitiesSelectedRow(
														measurabilityProject
													);
													await mutateActivateMeasurabilityProject(
														{
															active:
																!measurabilityProject.active,
															ids: [
																measurabilityProject.id,
															],
														}
													);
												}}
											/>
										)}
									</Flex>
								</Flex>
							</Flex>
							<Flex gap={'16px'} align={'center'}>
								<Flex direction={'column'} flex={1}>
									<Flex justify={'end'} flex={1}>
										<Tooltip
											arrowSize={8}
											withArrow
											label={
												<Stack gap={'5px'} w={'150px'}>
													<Flex
														gap={'15px'}
														justify={'space-between'}
														align={'center'}
													>
														<Text
															span
															variant={'caption'}
															c={'white'}
														>
															{t('global.partInuk')}
														</Text>
														<NumberFormatter
															thousandSeparator=" "
															decimalSeparator=","
															value={
																sellPrice_europertco2eq *
																0.2
															}
															decimalScale={2}
															suffix="€"
														/>
													</Flex>
													<Flex
														gap={'15px'}
														justify={'space-between'}
														align={'center'}
													>
														<Text
															span
															variant={'caption'}
															c={'white'}
														>
															{t('global.partProject')}
														</Text>
														<NumberFormatter
															thousandSeparator=" "
															decimalSeparator=","
															value={
																sellPrice_europertco2eq *
																0.4
															}
															decimalScale={2}
															suffix="€"
														/>
													</Flex>
													<Flex
														gap={'15px'}
														justify={'space-between'}
														align={'center'}
													>
														<Text
															span
															variant={'caption'}
															c={'white'}
														>
															{t(
																'global.partAdditionality'
															)}
														</Text>
														<NumberFormatter
															thousandSeparator=" "
															decimalSeparator=","
															value={
																sellPrice_europertco2eq *
																0.4
															}
															decimalScale={2}
															suffix="€"
														/>
													</Flex>
												</Stack>
											}
										>
											<Box>
												<Title
													order={4}
													c="var(--mantine-color-igluSurface-9)"
													style={{
														whiteSpace: 'nowrap',
													}}
												>
													<NumberFormatter
														thousandSeparator=" "
														decimalSeparator=","
														value={
															sellPrice_europertco2eq
														}
														decimalScale={2}
													/>
													<Text
														variant="body2-medium"
														size="18px"
														span
														c={'#FDFDFD'}
													>
														{` ${t('global.sellPriceUnit')}`}
													</Text>
												</Title>
											</Box>
										</Tooltip>
									</Flex>
									<Flex justify={'end'} flex={1}>
										<Text
											variant="body1"
											c="var(--mantine-color-igluTextButton-9)"
											style={{
												whiteSpace: 'nowrap',
											}}
										>
											{t('global.sellPrice')}{' '}
										</Text>
									</Flex>
								</Flex>
								<Divider
									color="white"
									size="1px"
									orientation="vertical"
								/>
								<Flex direction={'column'} flex={1}>
									<Flex flex={1} wrap={'nowrap'}>
										<Title
											order={4}
											c="var(--mantine-color-igluSurface-9)"
											style={{
												whiteSpace: 'nowrap',
											}}
										>
											<NumberFormatter
												thousandSeparator=" "
												decimalSeparator=","
												value={remaining_aco2}
												decimalScale={2}
											/>
											<Text
												size="18px"
												span
												c={'#FDFDFD'}
											>
												{` ${getUnitLabel('tco2e')}`}
											</Text>
										</Title>
									</Flex>
									<Flex flex={1}>
										<Text
											style={{
												whiteSpace: 'nowrap',
											}}
											variant="body1"
											c="var(--mantine-color-igluTextButton-9)"
										>
											{t('global.availableStock')}
										</Text>
									</Flex>
								</Flex>
							</Flex>
						</Flex>
						<Flex>
							<Flex flex={1} gap={'16px'}>
								<Text
									variant="body1"
									c="var(--mantine-color-igluTextButton-9)"
								>
									{t('global.type')}
									{' : '}
									<Text
										span
										variant="body2"
										c="var(--mantine-color-igluSurface-9)"
									>
										{t(technology)}
									</Text>
								</Text>
							</Flex>
							{checkAcls([ACLS.MEASURABILITIES.UPDATE]) &&
								!checkRole([
									ROLES.ORGANIZATION_ADMIN,
									ROLES.BRAND_ADMIN,
									ROLES.PRODUCER_ADMIN,
									ROLES.PRODUCER_STAFF,
								]) && (
									<Flex justify={'end'} flex={1}>
										<Button disabled variant={'primary'}>
											{t('global.edit')}
										</Button>
									</Flex>
								)}
						</Flex>
					</Flex>
				</Flex>
			</Box>

			<Flex
				style={{
					zIndex: '10',
					borderRadius: '32px',
					padding: '0px 30px',
				}}
				mt={'-60px'}
				direction={'column'}
				flex={1}
			>
				<Tabs
					defaultValue="generalInformations"
					classNames={classesTabs}
				>
					<Tabs.List>
						<Tabs.Tab value="generalInformations">
							{t('global.generalInformations')}
						</Tabs.Tab>
						{checkAclFront(
							ACLS.FRONT.MEASURABILITIES
								.TAB_TRANSACTION_HISTORY,
							role as ROLES
						) && (
							<Tabs.Tab value="transactionHistory">
								{t('global.transactionHistory')}
							</Tabs.Tab>
						)}
						{checkAclFront(
							ACLS.FRONT.MEASURABILITIES.TAB_SITES,
							role as ROLES
						) && (
							<Tabs.Tab value="settings">
								{t('global.sites')}{' '}
							</Tabs.Tab>
						)}
					</Tabs.List>
					{/* Infos générales */}
					<Tabs.Panel
						value="generalInformations"
						p={'20px'}
					>
						<Flex flex={1} gap={'24px'} wrap={'wrap'}>
							<Flex
								style={{
									flex: isSmall ? '1 1 100%' : 'none',
								}}
								bg={'#FDFDFD'}
							>
								<Flex
									className={classes.containerInfoBloc}
								>
									<Title
										order={5}
										c="var(--mantine-color-igluPrimary-9)"
										mb={'24px'}
									>
										{t('global.producerInfo')}
									</Title>
									<Flex
										style={{
											width: '100%',
										}}
										direction={'column'}
										gap={'8px'}
									>
										{contactProducer?.firstName &&
											contactProducer?.firstName !=
												NOT_APPLICABLE && (
												<Flex
													style={{
														width: '100%',
													}}
													flex={1}
												>
													<Flex
														className={
															classes.colProducerInfoTitle
														}
														flex={3}
													>
														<Text
															span
															variant="body2"
															c="var(--mantine-color-igluSecondary-9)"
														>
															{t('global.name')}
														</Text>
													</Flex>
													<Flex flex={5}>
														<Text
															span
															variant="body2-medium"
															c="var(--mantine-color-igluTextPrimary-9)"
														>
															{contactProducer
																? `${contactProducer.firstName} ${contactProducer.lastName}`
																: NOT_APPLICABLE}
														</Text>
													</Flex>
												</Flex>
											)}
										{entity?.name &&
											entity?.name != NOT_APPLICABLE && (
												<Flex
													style={{
														width: '100%',
													}}
													flex={1}
												>
													<Flex
														className={
															classes.colProducerInfoTitle
														}
														flex={3}
													>
														<Text
															span
															variant="body2"
															c="var(--mantine-color-igluSecondary-9)"
														>
															{t('global.contact')}
														</Text>
													</Flex>
													<Flex flex={5}>
														<Text
															span
															variant="body2-medium"
															c="var(--mantine-color-igluTextPrimary-9)"
														>
															{entity.name
																? entity.name
																: NOT_APPLICABLE}
														</Text>
													</Flex>
												</Flex>
											)}
										<Flex
											style={{
												width: '100%',
											}}
											flex={1}
										>
											<Flex
												className={
													classes.colProducerInfoTitle
												}
												flex={3}
											>
												<Text
													span
													variant="body2"
													c="var(--mantine-color-igluSecondary-9)"
												>
													{t('global.location')}
												</Text>
											</Flex>
											<Flex
												align={'center'}
												gap={'5px'}
												flex={5}
											>
												<Avatar
													className={classes.logoCountry}
													radius={'xl'}
													src={`https://flagcdn.com/w40/${entity.location && entity.location.countryCode ? entity.location.countryCode.toLowerCase() : ''}.png`}
													size={'xs'}
												>
													<IconQuestionMark />
												</Avatar>

												<Text
													span
													variant="body2-medium"
													c="var(--mantine-color-igluTextPrimary-9)"
												>
													{getCountryName(
														entity.location &&
															entity.location.countryCode
															? entity.location
																	.countryCode
															: ''
													)}
												</Text>
											</Flex>
										</Flex>
										{entity?.legalIdentifier &&
											entity?.legalIdentifier !=
												NOT_APPLICABLE && (
												<Flex
													style={{
														width: '100%',
													}}
													flex={1}
												>
													<Flex
														flex={3}
														className={
															classes.colProducerInfoTitle
														}
													>
														<Text
															span
															variant="body2"
															c="var(--mantine-color-igluSecondary-9)"
														>
															{t(
																'projectDetails.producerLegalIdentifier'
															)}
														</Text>
													</Flex>
													<Flex flex={5}>
														<Text
															span
															variant="body2-medium"
															c="var(--mantine-color-igluTextPrimary-9)"
														>
															{entity &&
															entity.legalIdentifier
																? entity.legalIdentifier
																: NOT_APPLICABLE}
														</Text>
													</Flex>
												</Flex>
											)}
									</Flex>
								</Flex>
							</Flex>
							<Flex
								bg={'#FDFDFD'}
								flex={1}
								direction={'column'}
								gap={'24px'}
							>
								<Flex className={classes.containerBloc}>
									<Title
										order={5}
										c="var(--mantine-color-igluPrimary-9)"
										mb={'24px'}
									>
										{t('global.description')}
									</Title>
									<div>
										{description ? (
											<Spoiler
												onExpandedChange={(opened) =>
													setIsExpanded(opened)
												}
												maxHeight={120}
												showLabel={t('global.showMore')}
												hideLabel={t('global.hide')}
												styles={{
													control: {
														fontFamily:
															'InterDisplay-SemiBold',
														color:
															'var(--mantine-color-igluPrimary-9)',
													},
												}}
											>
												<DescriptionRenderer
													description={description}
												/>
												{!isExpanded && (
													<div
														style={{
															position: 'absolute',
															bottom: 0,
															left: 0,
															width: '100%',
															height: '40px',
														}}
													/>
												)}
											</Spoiler>
										) : (
											<Text
												span
												variant="body1"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{NOT_APPLICABLE}
											</Text>
										)}
									</div>
								</Flex>
								<Flex className={classes.containerBloc}>
									<Flex
										flex={1}
										justify={'space-between'}
										align={'center'}
										style={{
											width: '100%',
										}}
										mb={'24px'}
									>
										<Title
											order={5}
											c="var(--mantine-color-igluPrimary-9)"
										>
											{t(
												'global.downloadTechnicalDocuments'
											)}
										</Title>
									</Flex>
									<Flex>
										{publicSpecs &&
										Array.isArray(publicSpecs) &&
										publicSpecs.length > 0 ? (
											<Group>
												{publicSpecs.map(
													(
														fileMetaData: TFileMetadata
													) => (
														<DownloadTag
															label={
																fileMetaData.filename
															}
															url={fileMetaData.link}
														/>
													)
												)}
											</Group>
										) : (
											<Text
												span
												variant="body1"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{NOT_APPLICABLE}
											</Text>
										)}
									</Flex>
								</Flex>
							</Flex>
						</Flex>
					</Tabs.Panel>
					<Tabs.Panel
						value="transactionHistory"
						p={'20px'}
					>
						<Flex direction={'column'} flex={1}>
							{checkRole([
								ROLES.SUPERADMIN,
								ROLES.ADMIN,
								ROLES.STAFF,
							]) && (
								<MintHistoryList
									projectId={measurabilityProject.id}
								/>
							)}
						</Flex>
					</Tabs.Panel>
					{/* Sites */}
					<Tabs.Panel value="settings" p={'20px'}>
						<Flex flex={1}>
							<Flex flex={1} bg={'#FDFDFD'}>
								{checkRole([
									ROLES.SUPERADMIN,
									ROLES.ADMIN,
									ROLES.STAFF,
								]) && (
									<SitesByProject
										data={getSearchableSites(
											sites,
											allSites.map(
												(item: TSite) => item.id
											)
										)}
										onSearchSite={(siteName) => {
											setSiteQuery(siteName);
										}}
									/>
								)}
							</Flex>
						</Flex>
					</Tabs.Panel>
				</Tabs>
			</Flex>
		</Box>
	);
};

export default MeasurabilityProjectDetails;
